import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarIcon, CheckCircle2 } from "lucide-react";
import { format, parseISO, startOfDay } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICES, TIME_SLOTS } from "@/lib/content";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

const today = () => new Date().toISOString().slice(0, 10);

export default function Book() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    patient_name: user?.name || "",
    phone: user?.phone || "",
    service: SERVICES[0].title,
    date: today(),
    time_slot: TIME_SLOTS[0],
    notes: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (!form.date) return;
    let cancelled = false;
    setLoadingSlots(true);
    api
      .get("/availability", { params: { date: form.date } })
      .then(({ data }) => {
        if (cancelled) return;
        setSlots(data.slots);
        setForm((f) => {
          const current = data.slots.find((s) => s.time_slot === f.time_slot);
          if (current?.available) return f;
          const firstFree = data.slots.find((s) => s.available);
          return { ...f, time_slot: firstFree ? firstFree.time_slot : "" };
        });
      })
      .catch(() => !cancelled && setSlots([]))
      .finally(() => !cancelled && setLoadingSlots(false));
    return () => {
      cancelled = true;
    };
  }, [form.date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.time_slot) {
      setError("Please choose an available time slot.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/appointments", form);
      setDone(data.appointment);
      toast.success("Appointment request received");
    } catch (err) {
      setError(apiError(err.response?.data?.detail, err.message));
      api
        .get("/availability", { params: { date: form.date } })
        .then(({ data }) => setSlots(data.slots))
        .catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="booking-confirmation">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
        <h1 className="mt-6 text-3xl sm:text-4xl tracking-tight text-slate-900">Appointment requested</h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Thank you, {done.patient_name}. We have your request for <strong>{done.service}</strong> on{" "}
          <strong>{done.date}</strong> at <strong>{done.time_slot}</strong>. Our team will call you on{" "}
          {done.phone} to confirm.
        </p>
        <p className="mt-4 text-sm text-slate-600" data-testid="confirmation-email-note">
          A confirmation email is on its way to you, and the clinic has been notified.
        </p>

        <p className="mt-4 text-sm text-slate-500">
          Need to change something? Call us at{" "}
          <span className="text-slate-800" data-testid="confirmation-clinic-phone">
            {settings.phone}
          </span>
          .
        </p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Button
            onClick={() => navigate("/my-bookings")}
            className="rounded-full h-11 px-7 bg-blue-900 text-white hover:bg-blue-950 hover:text-white"
            data-testid="confirmation-my-bookings-btn"
          >
            View My Bookings
          </Button>
          <Button variant="outline" className="rounded-full h-11 px-7" onClick={() => setDone(null)}>
            Book Another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-20" data-testid="book-page">
      <h1 className="text-3xl sm:text-4xl tracking-tight text-slate-900">Book Appointment</h1>
      <p className="mt-3 text-sm text-slate-600">
        Scheduling only — there is no payment step. We confirm every request by phone.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-6 bg-white border border-slate-100 rounded-2xl p-8">
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="patient_name">Patient name</Label>
            <Input
              id="patient_name"
              required
              value={form.patient_name}
              data-testid="book-name-input"
              onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
              className="rounded-xl h-11 focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Mobile number</Label>
            <Input
              id="phone"
              required
              value={form.phone}
              data-testid="book-phone-input"
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="rounded-xl h-11 focus:ring-2 focus:ring-sky-500"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Treatment needed</Label>
          <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
            <SelectTrigger className="rounded-xl h-11" data-testid="book-service-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem key={s.key} value={s.title} data-testid={`book-service-option-${s.key}`}>
                  {s.title}
                </SelectItem>
              ))}
              <SelectItem value="Not sure — need a consultation">Not sure — need a consultation</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Preferred date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="book-date-trigger"
                  className="w-full h-11 rounded-xl justify-start font-normal border-slate-300 hover:bg-slate-50"
                >
                  <CalendarIcon className="w-4 h-4 mr-2 text-sky-500" />
                  {format(parseISO(form.date), "EEE, d MMM yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={parseISO(form.date)}
                  defaultMonth={parseISO(form.date)}
                  disabled={{ before: startOfDay(new Date()) }}
                  onSelect={(d) => d && setForm({ ...form, date: format(d, "yyyy-MM-dd") })}
                  data-testid="book-date-calendar"
                />
              </PopoverContent>
            </Popover>
            <input type="hidden" data-testid="book-date-input" value={form.date} readOnly />
          </div>
          <div className="space-y-2">
            <Label>Preferred time</Label>
            {loadingSlots ? (
              <div className="h-11 rounded-xl bg-slate-100 animate-pulse" data-testid="book-slots-loading" />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="book-slot-grid">
                {slots.map((s) => (
                  <button
                    key={s.time_slot}
                    type="button"
                    disabled={!s.available}
                    onClick={() => setForm({ ...form, time_slot: s.time_slot })}
                    data-testid={`book-slot-${s.time_slot.replace(/[^0-9A-Za-z]/g, "")}`}
                    className={`h-11 rounded-xl text-sm border transition-colors duration-200 ${
                      !s.available
                        ? "border-slate-200 bg-slate-100 text-slate-400 line-through cursor-not-allowed"
                        : form.time_slot === s.time_slot
                        ? "border-blue-900 bg-blue-900 text-white"
                        : "border-slate-300 bg-white text-slate-700 hover:border-sky-500 hover:text-sky-600"
                    }`}
                  >
                    {s.time_slot}
                  </button>
                ))}
              </div>
            )}
            {!loadingSlots && slots.length > 0 && !slots.some((s) => s.available) && (
              <p className="text-sm text-amber-700" data-testid="book-no-slots">
                All slots are full on this date. Please pick another day.
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes">Anything we should know? (optional)</Label>
          <Textarea
            id="notes"
            rows={4}
            value={form.notes}
            data-testid="book-notes-input"
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="rounded-xl focus:ring-2 focus:ring-sky-500"
            placeholder="Pain, swelling, past treatment, medication…"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600" data-testid="book-error">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={busy}
          data-testid="book-submit-btn"
          className="w-full rounded-full h-12 bg-blue-900 text-white hover:bg-blue-950 hover:text-white"
        >
          {busy ? "Sending request…" : "Request Appointment"}
        </Button>
      </form>
    </div>
  );
}
